import ObjectTest from '../../objects/ObjectTest';
import {
    UNIT
} from '../../../../constants/size';
import { _openLinkBlank } from '../../../../utils/browse';
import ObjectsHolder from '../../objects/ObjectsHolder';
import { ObjectFactory } from '../../factories/ObjectFactory';
import { ActionTypes } from '../../../../constants/ActionTypes';

const THREE = require('three');

class AbstractUnit extends ObjectsHolder {
    constructor(data, webgl, stgs, markers = false) {
        super({ ...data, markers }, webgl, stgs, ObjectFactory);
    }

    _setup(data, stgs, webgl) {
        super._setup(data, stgs, webgl);

        this.unitData = data.unit;

        // Set to true and add method start to initialize user interaction
        this.hasInteraction = this.clickable;

        // Set to true and add method focusAt to enable focus state animation
        this.canFocusAt = false;

        // Set mainView reference to rich the interaction hit area (object)
        this.mainView = null;
        this.mainClickTarget = null;

        // Default flags
        this.hasProgressAnimation = false;
        this.hasObjectsProgressAnimation = false;

        this._objectsReady = false;
        this._ready = false;
        this._enabled = false;
    }

    _constructView(data, webgl, stgs, factory, orientation) {
        super._constructView(data, webgl, stgs, factory, orientation);

        const { markers } = data;

        if (markers) this._addMarkers();
    }

    _handleReady() {
        super._handleReady();

        if (this.clickable && this._started) {
            this._startInteraction();
        }
    }

    _addMarkers() {
        const colors = {
            header: 0xFFFF00,
            sectionHeader: 0xFF0000,
            text: 0xEDEDED,
            preview: 0x0000FF,
            ARFilter: 0x00FF00,
            object: 0x003366
        };

        const color = colors[this.data.type] ?? 0xFF0000;
        const start = new ObjectTest(5, 400, color * 0.5);
        const viewMarker = new ObjectTest(5, 100, color);
        const end = new ObjectTest(5, 400, color * 0.5);

        start.position.x = -200;
        viewMarker.position.x = start.position.x;
        end.position.x = start.position.x;

        start.position.z = 0;
        viewMarker.position.z = -this.mainViewZ;
        end.position.z = -this.zSize;

        this.add(start);
        this.add(viewMarker);
        this.add(end);
    }

    start() {
        super.start();

        if (this.clickable && this._ready) {
            this._startInteraction();
        }
    }

    _startInteraction() {
        if (!this.clickActions || !this.mainClickTarget) return;

        const targets = this.mainClickTarget;

        this.layers.set(this._layerChannel);
        targets.forEach((target, index) => {
            target.layers.set(this._layerChannel);
            target.cursor = 'pointer';
            target.on('click', () => {
                this.callAction(this.clickActions[index]);
            });
        });
    }

    callAction(action) {
        if (!action) return;

        const {
            key, href, target, workId
        } = action;

        switch (key) {
            case ActionTypes.LINK:
                _openLinkBlank(href, target);

                break;

            case ActionTypes.SHOW_WORK_DETAILS:
                this.dispatchEvent({
                    type: ActionTypes.SHOW_WORK_DETAILS,
                    workId
                });

                break;

            default:
                break;
        }
    }

    resize(winWidth, winHeight, orientation, force, pixelRatio) {
        super.resize(winWidth, winHeight, orientation, force, pixelRatio);

        if (this.objectsGroup) {
            this.objectsGroup.position.z = -this.mainViewZ;
        }
    }

    updateAnimationProgress() {
        if (!this.needProgressUpdate) return;

        const { camera } = this.webgl;

        if (!camera) return;

        const target = this.mainView || this;
        const mtx = target.matrixWorld;
        const worldPosition = new THREE.Vector3();

        worldPosition.setFromMatrixPosition(mtx);

        const zStart = worldPosition.z - camera.position.z - UNIT.camDistance;

        this.progress = (zStart * 0.5) / this.zSize;
    }

    get needProgressUpdate() {
        return this.hasProgressAnimation || this.hasObjectsProgressAnimation;
    }

    get totalWeight() {
        return this.unitData.weight;
    }

    get mainViewZ() {
        const viewShift = this.unitData.shift ?? 0;

        return this.zSize * (0.5 + viewShift);
    }

    get zSize() {
        return this.totalWeight * UNIT.zSize;
    }

    get enabled() {
        return this._enabled;
    }

    set enabled(value) {
        this._enabled = value;
    }
}

export default AbstractUnit;
